import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Clock,
  User,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  Mail,
  Phone,
  FileText,
  Loader2,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  getBusinessInfo,
  getLocations,
  getServices,
  getProviders,
  getAvailableDays,
  getAvailableSlots,
  createBooking,
  createPaymentIntent,
  getIcsDownloadUrl,
  type BusinessInfo,
  type Location,
  type ServiceCategory,
  type Service,
  type Provider,
  type TimeSlot,
  type BookingResponse,
} from "@/lib/booking-api";
import { ChatWidget } from "@/components/ChatWidget";
import { PhoneInput } from "@/components/PhoneInput";
import { BlobBackground } from "@/components/ui";
import { generateBlobColors } from "@/lib/branding-context";

// Initialize Stripe
const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null;

// ════════════════════════════════════════════════════════════════════════════
// Booking Wizard Page
// ════════════════════════════════════════════════════════════════════════════

type Step = "location" | "service" | "provider" | "datetime" | "contact" | "payment" | "confirmation";

// Interface for chatbot pre-fill params
interface ChatbotParams {
  serviceId: string;
  providerId: string;
  locationId: string;
  date: string;
  time: string;
  name: string;
  email: string;
  phone: string;
}

export default function BookingWizard() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  // Check if coming from chatbot with pre-filled data
  const fromChat = searchParams.get("fromChat") === "true";

  // Memoize chatbot params to prevent infinite re-renders
  const chatbotParams = useMemo<ChatbotParams | null>(() => {
    if (!fromChat) return null;
    return {
      serviceId: searchParams.get("serviceId") || "",
      providerId: searchParams.get("providerId") || "",
      locationId: searchParams.get("locationId") || "",
      date: searchParams.get("date") || "",
      time: searchParams.get("time") || "",
      name: searchParams.get("name") || "",
      email: searchParams.get("email") || "",
      phone: searchParams.get("phone") || "",
    };
  }, [fromChat, searchParams]);

  // Track chatbot initialization state
  const [chatbotInitialized, setChatbotInitialized] = useState(false);

  // Business info
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Step data
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);

  // Selections
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [expandedServiceIds, setExpandedServiceIds] = useState<Set<string>>(new Set());
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  // Contact info
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientNotes, setClientNotes] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);

  // Payment state
  const [depositRequired, setDepositRequired] = useState(false);
  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // Booking result
  const [bookingResult, setBookingResult] = useState<BookingResponse | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  // Current step
  const [currentStep, setCurrentStep] = useState<Step>("location");

  // Check if payment step should be shown
  const showPaymentStep = depositRequired && stripePromise && clientSecret;

  // Steps configuration
  const steps: { id: Step; label: string }[] = useMemo(() => {
    const baseSteps: { id: Step; label: string }[] = [
      { id: "location", label: "Location" },
      { id: "service", label: "Service" },
      { id: "provider", label: "Provider" },
      { id: "datetime", label: "Date & Time" },
      { id: "contact", label: "Your Info" },
      { id: "payment", label: "Payment" },
      { id: "confirmation", label: "Confirmed" },
    ];

    let filteredSteps = baseSteps;

    // Skip location step if only one location
    if (locations.length <= 1) {
      filteredSteps = filteredSteps.filter((s) => s.id !== "location");
    }

    // Skip payment step if no deposit required or Stripe not configured
    if (!showPaymentStep) {
      filteredSteps = filteredSteps.filter((s) => s.id !== "payment");
    }

    return filteredSteps;
  }, [locations, showPaymentStep]);

  // Load business info and locations
  useEffect(() => {
    async function loadInitialData() {
      if (!slug) return;

      try {
        setLoading(true);
        const [info, locs] = await Promise.all([
          getBusinessInfo(slug),
          getLocations(slug),
        ]);
        setBusinessInfo(info);
        setLocations(locs);

        // If coming from chatbot, don't auto-navigate - let chatbot init handle it
        if (fromChat && chatbotParams) {
          // Find and select the location from chatbot params
          const location = locs.find((l) => l.id === chatbotParams.locationId);
          if (location) {
            setSelectedLocation(location);
          } else if (locs.length === 1 && locs[0]) {
            setSelectedLocation(locs[0]);
          }
        } else {
          // Auto-select if only one location (normal flow)
          if (locs.length === 1 && locs[0]) {
            setSelectedLocation(locs[0]);
            setCurrentStep("service");
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load business info");
      } finally {
        setLoading(false);
      }
    }
    loadInitialData();
  }, [slug, fromChat, chatbotParams]);

  // Initialize from chatbot params - runs a chain of data loading and selection
  useEffect(() => {
    async function initFromChatbot() {
      if (!fromChat || !chatbotParams || !slug || !selectedLocation || chatbotInitialized) return;

      try {
        // Step 1: Load services and find the selected one
        const cats = await getServices(slug, selectedLocation.id);
        setCategories(cats);

        let foundService: Service | null = null;
        for (const cat of cats) {
          const service = cat.services.find((s) => s.id === chatbotParams.serviceId);
          if (service) {
            foundService = service;
            break;
          }
        }

        if (!foundService) {
          console.error("Chatbot service not found:", chatbotParams.serviceId);
          setChatbotInitialized(true);
          setCurrentStep("service");
          return;
        }
        setSelectedService(foundService);

        // Step 2: Load providers and find the selected one
        const provs = await getProviders(slug, foundService.id, selectedLocation.id);
        setProviders(provs);

        const provider = provs.find((p) => p.id === chatbotParams.providerId);
        if (!provider) {
          console.error("Chatbot provider not found:", chatbotParams.providerId);
          setChatbotInitialized(true);
          setCurrentStep("provider");
          return;
        }
        setSelectedProvider(provider);

        // Step 3: Set the date and load time slots
        setSelectedDate(chatbotParams.date);

        const slots = await getAvailableSlots(
          slug,
          foundService.id,
          provider.id,
          selectedLocation.id,
          chatbotParams.date
        );
        setTimeSlots(slots);

        const timeSlot = slots.find((s) => s.startTime === chatbotParams.time);
        if (!timeSlot) {
          console.error("Chatbot time slot not found:", chatbotParams.time);
          setChatbotInitialized(true);
          setCurrentStep("datetime");
          return;
        }
        setSelectedTime(timeSlot);

        // Step 4: Set contact info
        setClientName(chatbotParams.name);
        setClientEmail(chatbotParams.email);
        setClientPhone(chatbotParams.phone);

        // Step 5: Mark as initialized and go directly to contact step
        // (which will trigger payment check and then show payment form)
        setChatbotInitialized(true);
        setCurrentStep("contact");
      } catch (err) {
        console.error("Failed to initialize from chatbot:", err);
        setChatbotInitialized(true);
        setCurrentStep("service");
      }
    }

    initFromChatbot();
  }, [fromChat, chatbotParams, slug, selectedLocation, chatbotInitialized]);

  // Load services when location is selected
  useEffect(() => {
    async function loadServices() {
      if (!slug || !selectedLocation) return;

      try {
        const cats = await getServices(slug, selectedLocation.id);
        setCategories(cats);
      } catch (err) {
        console.error("Failed to load services:", err);
      }
    }
    loadServices();
  }, [slug, selectedLocation]);

  // Load providers when service is selected
  useEffect(() => {
    async function loadProviders() {
      if (!slug || !selectedService || !selectedLocation) return;

      try {
        const provs = await getProviders(slug, selectedService.id, selectedLocation.id);
        setProviders(provs);
      } catch (err) {
        console.error("Failed to load providers:", err);
      }
    }
    loadProviders();
  }, [slug, selectedService, selectedLocation]);

  // Load available days when provider is selected
  useEffect(() => {
    async function loadAvailability() {
      if (!slug || !selectedService || !selectedProvider || !selectedLocation) return;

      try {
        const days = await getAvailableDays(
          slug,
          selectedService.id,
          selectedProvider.id,
          selectedLocation.id,
          currentMonth
        );
        setAvailableDays(days);
      } catch (err) {
        console.error("Failed to load availability:", err);
      }
    }
    loadAvailability();
  }, [slug, selectedService, selectedProvider, selectedLocation, currentMonth]);

  // Load time slots when date is selected
  useEffect(() => {
    async function loadSlots() {
      if (!slug || !selectedService || !selectedProvider || !selectedLocation || !selectedDate)
        return;

      try {
        const slots = await getAvailableSlots(
          slug,
          selectedService.id,
          selectedProvider.id,
          selectedLocation.id,
          selectedDate
        );
        setTimeSlots(slots);
      } catch (err) {
        console.error("Failed to load time slots:", err);
      }
    }
    loadSlots();
  }, [slug, selectedService, selectedProvider, selectedLocation, selectedDate]);

  // Check for deposit requirement when moving to contact step
  // Only run once when entering contact step, not on every email change
  const [paymentChecked, setPaymentChecked] = useState(false);

  useEffect(() => {
    // Reset payment checked state when going back to earlier steps
    if (currentStep !== "contact" && currentStep !== "payment") {
      setPaymentChecked(false);
    }
  }, [currentStep]);

  useEffect(() => {
    async function checkPaymentRequired() {
      if (!slug || !selectedService || currentStep !== "contact" || paymentChecked) return;

      // Reset payment state
      setPaymentError(null);
      setPaymentComplete(false);

      try {
        setPaymentLoading(true);
        const result = await createPaymentIntent(slug, selectedService.id, clientEmail || undefined);

        if (result.depositRequired && result.clientSecret && result.paymentIntentId) {
          setDepositRequired(true);
          setDepositAmount(result.depositAmount || 0);
          setClientSecret(result.clientSecret);
          setPaymentIntentId(result.paymentIntentId);
        } else {
          // No deposit required - either disabled or Stripe not connected
          setDepositRequired(false);
          setClientSecret(null);
          setPaymentIntentId(null);
        }
        setPaymentChecked(true);
      } catch (err) {
        console.error("Failed to check payment requirement:", err);
        // Show error to user instead of silently proceeding
        const errorMessage = err instanceof Error ? err.message : "Failed to initialize payment";
        setPaymentError(errorMessage);
        // Still mark as checked so we don't keep retrying
        setPaymentChecked(true);
        // Keep depositRequired as false so user can at least see the form
        setDepositRequired(false);
      } finally {
        setPaymentLoading(false);
      }
    }
    checkPaymentRequired();
  }, [slug, selectedService, currentStep, paymentChecked, clientEmail]);

  // Auto-proceed to payment step when coming from chatbot
  useEffect(() => {
    // Only auto-proceed if:
    // 1. Coming from chatbot
    // 2. We're on the contact step
    // 3. Payment has been checked
    // 4. Payment is required and Stripe is configured
    // 5. All required contact info is filled
    if (
      fromChat &&
      chatbotInitialized &&
      currentStep === "contact" &&
      paymentChecked &&
      !paymentLoading &&
      depositRequired &&
      stripePromise &&
      clientSecret &&
      clientName &&
      clientEmail
    ) {
      // Auto-proceed to payment step
      setCurrentStep("payment");
    }
  }, [
    fromChat,
    chatbotInitialized,
    currentStep,
    paymentChecked,
    paymentLoading,
    depositRequired,
    clientSecret,
    clientName,
    clientEmail,
  ]);

  // Handle booking submission
  async function handleBooking(confirmedPaymentIntentId?: string) {
    if (
      !slug ||
      !selectedService ||
      !selectedProvider ||
      !selectedLocation ||
      !selectedDate ||
      !selectedTime
    )
      return;

    // If deposit is required AND Stripe is configured AND payment not complete, don't proceed
    // Allow booking without payment when Stripe isn't configured
    if (depositRequired && stripePromise && clientSecret && !paymentComplete && !confirmedPaymentIntentId) {
      setPaymentError("Please complete the payment to continue");
      return;
    }

    setBookingLoading(true);
    try {
      const result = await createBooking(slug, {
        serviceId: selectedService.id,
        providerId: selectedProvider.id,
        locationId: selectedLocation.id,
        date: selectedDate,
        startTime: selectedTime.startTime,
        clientName,
        clientEmail,
        clientPhone: clientPhone || undefined,
        clientNotes: clientNotes || undefined,
        paymentIntentId: confirmedPaymentIntentId || paymentIntentId || undefined,
        smsOptIn: smsOptIn && !!clientPhone,
      });
      setBookingResult(result);
      setCurrentStep("confirmation");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create booking");
    } finally {
      setBookingLoading(false);
    }
  }

  // Handle ICS file download (cross-origin safe)
  async function handleIcsDownload() {
    if (!slug || !bookingResult) return;

    const url = getIcsDownloadUrl(slug, bookingResult.appointment.confirmationCode);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to download calendar file");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `appointment-${bookingResult.appointment.confirmationCode}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download ICS:", err);
      // Fallback: open in new tab
      window.open(url, "_blank");
    }
  }

  // Format time for display
  const formatTime = (time: string) => {
    const parts = time.split(":");
    const hours = parts[0] ?? "00";
    const minutes = parts[1] ?? "00";
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Navigate steps
  const goBack = () => {
    const stepIndex = steps.findIndex((s) => s.id === currentStep);
    if (stepIndex > 0) {
      const prevStep = steps[stepIndex - 1];
      if (prevStep) {
        setCurrentStep(prevStep.id);
      }
    }
  };

  // Brand colors - use extracted primary color, fallback to branding primary, then default
  const brandPrimary: string = useMemo(() => {
    const extracted = businessInfo?.branding?.extractedColors;
    const firstExtracted = extracted?.[0];
    if (firstExtracted) {
      return firstExtracted; // Use first extracted color as primary
    }
    return businessInfo?.branding.primaryColor ?? "#273240";
  }, [businessInfo?.branding?.extractedColors, businessInfo?.branding.primaryColor]);

  // Generate blob colors from extracted colors
  const blobColors = useMemo(() => {
    const extracted = businessInfo?.branding?.extractedColors;
    return extracted?.length ? generateBlobColors(extracted) : undefined;
  }, [businessInfo?.branding?.extractedColors]);

  const hidePricing = businessInfo?.hidePricing ?? false;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Show elegant loading overlay when coming from chatbot
  // This hides the step-by-step data loading process
  const showChatbotLoading = fromChat && (!chatbotInitialized || (currentStep === "contact" && !paymentChecked));

  if (showChatbotLoading && businessInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center px-8"
        >
          {/* Business Logo */}
          {businessInfo.logoUrl && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-6"
            >
              <img
                src={businessInfo.logoUrl}
                alt={businessInfo.name}
                className="h-16 w-auto mx-auto"
              />
            </motion.div>
          )}

          {/* Loading Animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <div className="relative w-16 h-16 mx-auto">
              {/* Outer ring */}
              <motion.div
                className="absolute inset-0 rounded-full border-4 border-gray-200"
                style={{ borderTopColor: brandPrimary }}
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              {/* Inner pulse */}
              <motion.div
                className="absolute inset-3 rounded-full"
                style={{ backgroundColor: brandPrimary + "20" }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Center dot */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: brandPrimary }}
                />
              </motion.div>
            </div>
          </motion.div>

          {/* Loading Text */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Preparing Your Booking
            </h2>
            <p className="text-gray-500 text-sm">
              Setting up your appointment details...
            </p>
          </motion.div>

          {/* Progress dots */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex justify-center gap-1.5 mt-6"
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: brandPrimary }}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: i * 0.2,
                  ease: "easeInOut",
                }}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (error && !bookingResult) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Custom blob background if business has extracted colors */}
      {blobColors && <BlobBackground colors={blobColors} />}

      {/* Main content wrapper - above blob background */}
      <div className="relative z-10">
      {/* Header */}
      <header
        className="py-4 px-6 shadow-sm"
        style={{ backgroundColor: brandPrimary }}
      >
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          {businessInfo?.logoUrl ? (
            <div className="bg-white rounded-lg p-1.5 shadow-sm">
              <img
                src={businessInfo.logoUrl}
                alt={businessInfo.name}
                className="h-8 w-auto"
              />
            </div>
          ) : (
            <h1 className="text-xl font-semibold text-white">{businessInfo?.name}</h1>
          )}
        </div>
      </header>

      {/* Progress Steps */}
      {currentStep !== "confirmation" && (
        <div className="bg-white border-b px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2">
              {steps
                .filter((s) => s.id !== "confirmation")
                .map((step, idx, arr) => {
                  const stepIndex = steps.findIndex((s) => s.id === currentStep);
                  const thisIndex = steps.findIndex((s) => s.id === step.id);
                  const isActive = step.id === currentStep;
                  const isCompleted = thisIndex < stepIndex;

                  return (
                    <div key={step.id} className="flex items-center">
                      <div
                        className={`
                          flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
                          transition-colors
                          ${isCompleted ? "text-green-600" : ""}
                          ${!isActive && !isCompleted ? "text-gray-400" : ""}
                        `}
                        style={isActive ? { backgroundColor: brandPrimary, color: "white" } : undefined}
                      >
                        {isCompleted ? (
                          <Check size={16} />
                        ) : (
                          <span className="w-5 h-5 flex items-center justify-center rounded-full border border-current text-xs">
                            {idx + 1}
                          </span>
                        )}
                        <span className="hidden sm:inline">{step.label}</span>
                      </div>
                      {idx < arr.length - 1 && (
                        <ChevronRight size={16} className="mx-1 text-gray-300" />
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {/* Location Step */}
          {currentStep === "location" && (
            <StepContainer key="location" title="Select a Location">
              <div className="grid gap-4 sm:grid-cols-2">
                {locations.map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => {
                      setSelectedLocation(loc);
                      setCurrentStep("service");
                    }}
                    className="p-4 rounded-xl border-2 text-left transition-all hover:shadow-md bg-white/80 backdrop-blur-sm"
                    style={{
                      borderColor: selectedLocation?.id === loc.id ? brandPrimary : "#e5e7eb",
                    }}
                    onMouseEnter={(e) => { if (selectedLocation?.id !== loc.id) e.currentTarget.style.borderColor = brandPrimary; }}
                    onMouseLeave={(e) => { if (selectedLocation?.id !== loc.id) e.currentTarget.style.borderColor = "#e5e7eb"; }}
                  >
                    <div className="flex items-start gap-3">
                      <MapPin className="text-gray-400 mt-1" size={20} />
                      <div>
                        <p className="font-medium text-gray-900">{loc.name}</p>
                        {loc.address && (
                          <p className="text-sm text-gray-500 mt-1">{loc.address}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </StepContainer>
          )}

          {/* Service Step */}
          {currentStep === "service" && (
            <StepContainer key="service" title="Select a Service" onBack={locations.length > 1 ? goBack : undefined}>
              <div className="space-y-6">
                {categories.map((cat) => (
                  <div key={cat.id ?? "other"}>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                      {cat.name}
                    </h3>
                    <div className="grid gap-3">
                      {cat.services.map((service) => {
                        const isExpanded = expandedServiceIds.has(service.id);
                        const selectService = () => {
                          setSelectedService(service);
                          setCurrentStep("provider");
                        };
                        return (
                          <div
                            key={service.id}
                            role="button"
                            tabIndex={0}
                            onClick={selectService}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                selectService();
                              }
                            }}
                            className="p-4 rounded-xl border-2 text-left transition-all hover:shadow-md bg-white/80 backdrop-blur-sm cursor-pointer"
                            style={{
                              borderColor: selectedService?.id === service.id ? brandPrimary : "#e5e7eb",
                            }}
                            onMouseEnter={(e) => { if (selectedService?.id !== service.id) e.currentTarget.style.borderColor = brandPrimary; }}
                            onMouseLeave={(e) => { if (selectedService?.id !== service.id) e.currentTarget.style.borderColor = "#e5e7eb"; }}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{service.name}</p>
                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Clock size={14} />
                                    {service.durationMinutes} min
                                  </span>
                                  {service.description && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedServiceIds((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(service.id)) {
                                            next.delete(service.id);
                                          } else {
                                            next.add(service.id);
                                          }
                                          return next;
                                        });
                                      }}
                                      aria-expanded={isExpanded}
                                      className="flex items-center gap-1 font-medium hover:underline"
                                      style={{ color: brandPrimary }}
                                    >
                                      {isExpanded ? "Hide Details" : "Learn More"}
                                      <ChevronDown
                                        size={14}
                                        className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                      />
                                    </button>
                                  )}
                                </div>
                                {service.description && isExpanded && (
                                  <p className="text-sm text-gray-500 mt-2">{service.description}</p>
                                )}
                              </div>
                              {!hidePricing && (
                                <p className="font-semibold text-gray-900">
                                  ${Number(service.price).toFixed(0)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </StepContainer>
          )}

          {/* Provider Step */}
          {currentStep === "provider" && (
            <StepContainer key="provider" title="Select a Provider" onBack={goBack}>
              <div className="grid gap-4 sm:grid-cols-2">
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => {
                      setSelectedProvider(provider);
                      setCurrentStep("datetime");
                    }}
                    className="p-4 rounded-xl border-2 text-left transition-all hover:shadow-md bg-white/80 backdrop-blur-sm"
                    style={{
                      borderColor: selectedProvider?.id === provider.id ? brandPrimary : "#e5e7eb",
                    }}
                    onMouseEnter={(e) => { if (selectedProvider?.id !== provider.id) e.currentTarget.style.borderColor = brandPrimary; }}
                    onMouseLeave={(e) => { if (selectedProvider?.id !== provider.id) e.currentTarget.style.borderColor = "#e5e7eb"; }}
                  >
                    <div className="flex items-start gap-4">
                      {provider.photoUrl ? (
                        <img
                          src={provider.photoUrl}
                          alt={provider.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <User size={24} className="text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{provider.name}</p>
                        {provider.title && (
                          <p className="text-sm text-gray-500">{provider.title}</p>
                        )}
                        {provider.bio && (
                          <p className="text-sm text-gray-500 mt-2 line-clamp-2">{provider.bio}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </StepContainer>
          )}

          {/* Date & Time Step */}
          {currentStep === "datetime" && (
            <StepContainer key="datetime" title="Select Date & Time" onBack={goBack}>
              <div className="grid gap-8 lg:grid-cols-2">
                {/* Calendar - with glass morphism (translucent to show blob effects) */}
                <div className="bg-white/50 backdrop-blur-md rounded-xl p-4 border border-white/30 shadow-sm">
                  <MiniCalendar
                    month={currentMonth}
                    availableDays={availableDays}
                    selectedDate={selectedDate}
                    onSelectDate={(date) => {
                      setSelectedDate(date);
                      setSelectedTime(null);
                    }}
                    onChangeMonth={setCurrentMonth}
                    brandPrimary={brandPrimary}
                  />
                </div>

                {/* Time Slots - with glass morphism (translucent to show blob effects) */}
                <div className="bg-white/50 backdrop-blur-md rounded-xl p-4 border border-white/30 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                    {selectedDate ? formatDate(selectedDate) : "Select a date"}
                  </h3>
                  {selectedDate && timeSlots.length === 0 && (
                    <p className="text-gray-500">No available times for this date</p>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot.startTime}
                        onClick={() => {
                          setSelectedTime(slot);
                          setCurrentStep("contact");
                        }}
                        className="px-3 py-2 rounded-lg border-2 text-sm font-semibold transition-all shadow-sm"
                        style={{
                          borderColor: selectedTime?.startTime === slot.startTime ? brandPrimary : brandPrimary + "40",
                          backgroundColor: selectedTime?.startTime === slot.startTime ? brandPrimary : "rgba(255,255,255,0.9)",
                          color: selectedTime?.startTime === slot.startTime ? "white" : brandPrimary,
                        }}
                        onMouseEnter={(e) => {
                          if (selectedTime?.startTime !== slot.startTime) {
                            e.currentTarget.style.borderColor = brandPrimary;
                            e.currentTarget.style.backgroundColor = brandPrimary + "15";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedTime?.startTime !== slot.startTime) {
                            e.currentTarget.style.borderColor = brandPrimary + "40";
                            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.9)";
                          }
                        }}
                      >
                        {formatTime(slot.startTime)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </StepContainer>
          )}

          {/* Contact Step */}
          {currentStep === "contact" && (
            <StepContainer key="contact" title="Your Information" onBack={goBack}>
              {/* Glass morphism container for contact form */}
              <div className="max-w-md bg-white/50 backdrop-blur-md rounded-xl p-6 border border-white/30 shadow-sm">
                {/* Booking Summary */}
                <div className="bg-white/80 rounded-xl p-4 mb-6 border border-white/40">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Booking Summary</h3>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-gray-900">{selectedService?.name}</p>
                    <p className="text-gray-500">with {selectedProvider?.name}</p>
                    <p className="text-gray-500">
                      {selectedDate && formatDate(selectedDate)} at{" "}
                      {selectedTime && formatTime(selectedTime.startTime)}
                    </p>
                    <p className="text-gray-500">{selectedLocation?.name}</p>
                    {!hidePricing && (
                      <p className="font-medium text-gray-900 mt-2">
                        ${selectedService && Number(selectedService.price).toFixed(0)}
                      </p>
                    )}
                    {depositRequired && depositAmount > 0 && (
                      <p className="text-green-600 font-medium">
                        Deposit required: ${depositAmount.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Contact Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                        placeholder="Jane Doe"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                        placeholder="jane@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <PhoneInput
                        value={clientPhone}
                        onChange={setClientPhone}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                      />
                    </div>
                    {/* SMS Opt-in Checkbox */}
                    {clientPhone && (
                      <label className="flex items-start gap-2 mt-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={smsOptIn}
                          onChange={(e) => setSmsOptIn(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        <span className="text-sm text-gray-600">
                          Yes, send me appointment reminders via text message.{" "}
                          <a
                            href={`/${slug}/sms-terms`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-900"
                          >
                            View SMS Terms
                          </a>
                        </span>
                      </label>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes for your appointment
                    </label>
                    <div className="relative">
                      <FileText size={18} className="absolute left-3 top-3 text-gray-400" />
                      <textarea
                        value={clientNotes}
                        onChange={(e) => setClientNotes(e.target.value)}
                        rows={3}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none text-gray-900 placeholder:text-gray-400"
                        placeholder="Any special requests or information..."
                      />
                    </div>
                  </div>

                  {/* Show loading state while checking payment requirements */}
                  {paymentLoading && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 size={24} className="animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Checking payment requirements...</span>
                    </div>
                  )}

                  {/* Show error when payment initialization failed */}
                  {paymentError && !paymentLoading && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle size={18} className="text-red-600 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-red-700 font-medium">Payment initialization error</span>
                        <p className="text-red-600 text-sm mt-1">{paymentError}</p>
                        <p className="text-red-600 text-sm mt-1">
                          Please contact the business directly to complete your booking.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Show warning when deposit required but Stripe not configured on frontend */}
                  {depositRequired && !stripePromise && !paymentLoading && !paymentError && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertCircle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-amber-700 font-medium">Payment system not configured</span>
                        <p className="text-amber-600 text-sm mt-1">
                          A ${depositAmount.toFixed(2)} deposit is required but online payment is unavailable.
                          You can still book and pay at your appointment.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Fine print — shown only when business has configured legal URLs */}
                  {(businessInfo?.termsOfServiceUrl || businessInfo?.privacyPolicyUrl) && (
                    <p className="text-xs text-gray-400 text-center leading-relaxed">
                      By booking, you agree to our{" "}
                      {businessInfo.termsOfServiceUrl ? (
                        <a href={businessInfo.termsOfServiceUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                          Terms of Service
                        </a>
                      ) : (
                        "Terms of Service"
                      )}
                      {businessInfo.termsOfServiceUrl && businessInfo.privacyPolicyUrl ? " and " : ""}
                      {businessInfo.privacyPolicyUrl ? (
                        <a href={businessInfo.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                          Privacy Policy
                        </a>
                      ) : (
                        !businessInfo.termsOfServiceUrl ? " and Privacy Policy" : ""
                      )}
                      .
                    </p>
                  )}

                  {/* Continue to Payment button - when Stripe is configured and deposit required */}
                  {showPaymentStep && !paymentLoading && (
                    <button
                      onClick={() => setCurrentStep("payment")}
                      disabled={!clientName || !clientEmail}
                      className="w-full py-3 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                      style={{ backgroundColor: brandPrimary }}
                    >
                      <CreditCard size={18} />
                      Continue to Payment
                      <ChevronRight size={18} />
                    </button>
                  )}

                  {/* Confirm Booking button - when no payment required or Stripe not configured */}
                  {!showPaymentStep && !paymentLoading && (
                    <button
                      onClick={() => handleBooking()}
                      disabled={!clientName || !clientEmail || bookingLoading}
                      className="w-full py-3 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                      style={{ backgroundColor: brandPrimary }}
                    >
                      {bookingLoading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Booking...
                        </>
                      ) : (
                        <>
                          <Check size={18} />
                          Confirm Booking
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </StepContainer>
          )}

          {/* Payment Step */}
          {currentStep === "payment" && clientSecret && stripePromise && (
            <StepContainer key="payment" title="Payment" onBack={goBack}>
              {/* Glass morphism container for payment form */}
              <div className="max-w-md bg-white/50 backdrop-blur-md rounded-xl p-6 border border-white/30 shadow-sm">
                {/* Booking Summary */}
                <div className="bg-white/80 rounded-xl p-4 mb-6 border border-white/40">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Booking Summary</h3>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-gray-900">{selectedService?.name}</p>
                    <p className="text-gray-500">with {selectedProvider?.name}</p>
                    <p className="text-gray-500">
                      {selectedDate && formatDate(selectedDate)} at{" "}
                      {selectedTime && formatTime(selectedTime.startTime)}
                    </p>
                    <p className="text-gray-500">{selectedLocation?.name}</p>
                    <div className="border-t border-gray-200 mt-3 pt-3">
                      {!hidePricing && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Service Price</span>
                          <span className="font-medium text-gray-900">
                            ${selectedService && Number(selectedService.price).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between mt-1">
                        <span className="text-green-600 font-medium">Deposit Due Now</span>
                        <span className="text-green-600 font-bold">
                          ${depositAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Info Summary */}
                <div className="bg-white/80 rounded-xl p-4 mb-6 border border-white/40">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Your Information</h3>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-gray-900">{clientName}</p>
                    <p className="text-gray-500">{clientEmail}</p>
                    {clientPhone && <p className="text-gray-500">{clientPhone}</p>}
                  </div>
                </div>

                {/* Stripe Payment Form */}
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard size={20} className="text-gray-600" />
                    <h4 className="font-medium text-gray-900">
                      Pay ${depositAmount.toFixed(2)} Deposit
                    </h4>
                  </div>
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: "stripe",
                        variables: {
                          colorPrimary: brandPrimary,
                        },
                      },
                    }}
                  >
                    <PaymentForm
                      clientName={clientName}
                      clientEmail={clientEmail}
                      onSuccess={(piId) => {
                        setPaymentComplete(true);
                        setPaymentError(null);
                        // Automatically submit booking after payment
                        handleBooking(piId);
                      }}
                      onError={(msg) => setPaymentError(msg)}
                      disabled={false}
                      loading={bookingLoading}
                      brandPrimary={brandPrimary}
                    />
                  </Elements>
                </div>

                {paymentError && (
                  <div className="flex items-center gap-2 p-3 mt-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle size={18} className="text-red-600" />
                    <span className="text-red-700">{paymentError}</span>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-4 text-center">
                  {hidePricing
                    ? "Your payment is secure and encrypted. Any remaining balance will be due at your appointment."
                    : `Your payment is secure and encrypted. The remaining balance of $${selectedService ? (Number(selectedService.price) - depositAmount).toFixed(2) : ""} will be due at your appointment.`}
                </p>
              </div>
            </StepContainer>
          )}

          {/* Confirmation Step */}
          {currentStep === "confirmation" && bookingResult && (
            <StepContainer key="confirmation" title="">
              <div className="text-center max-w-md mx-auto">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  Booking Confirmed!
                </h2>
                <p className="text-gray-500 mb-6">
                  Your confirmation code is{" "}
                  <span className="font-mono font-bold text-gray-900">
                    {bookingResult.appointment.confirmationCode}
                  </span>
                </p>

                <div className="bg-white rounded-xl p-6 text-left mb-6 shadow-sm border border-gray-200">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Service</p>
                      <p className="font-medium text-gray-900">{bookingResult.appointment.service.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Provider</p>
                      <p className="font-medium text-gray-900">{bookingResult.appointment.provider.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date & Time</p>
                      <p className="font-medium text-gray-900">
                        {formatDate(bookingResult.appointment.date)} at{" "}
                        {formatTime(bookingResult.appointment.startTime)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-medium text-gray-900">{bookingResult.appointment.location.name}</p>
                      {bookingResult.appointment.location.address && (
                        <p className="text-sm text-gray-600">
                          {bookingResult.appointment.location.address}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <a
                    href={bookingResult.links.googleCalendar}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-900 font-medium"
                  >
                    <Calendar size={18} />
                    Add to Google Calendar
                  </a>
                  <button
                    onClick={handleIcsDownload}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-900 font-medium"
                  >
                    <Calendar size={18} />
                    Download .ics file
                  </button>
                </div>

                <p className="text-sm text-gray-500 mt-6">
                  A confirmation email has been sent to{" "}
                  <span className="font-medium">{bookingResult.appointment.client.email}</span>
                </p>

                <p className="text-xs text-gray-400 mt-8">
                  Powered by{" "}
                  <a
                    href="https://appointmentflo.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-600"
                  >
                    AppointmentFlo.com
                  </a>
                </p>
              </div>
            </StepContainer>
          )}
        </AnimatePresence>
      </main>
      </div>

      {/* AI Chat Widget */}
      {slug && <ChatWidget slug={slug} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step Container Component
// ────────────────────────────────────────────────────────────────────────────

interface StepContainerProps {
  title: string;
  children: React.ReactNode;
  onBack?: () => void;
}

function StepContainer({ title, children, onBack }: StepContainerProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
    >
      {(title || onBack) && (
        <div className="flex items-center gap-4 mb-6">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {title && <h2 className="text-xl font-semibold text-gray-900">{title}</h2>}
        </div>
      )}
      {children}
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mini Calendar Component
// ────────────────────────────────────────────────────────────────────────────

interface MiniCalendarProps {
  month: string;
  availableDays: string[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  onChangeMonth: (month: string) => void;
  brandPrimary?: string;
}

function MiniCalendar({
  month,
  availableDays,
  selectedDate,
  onSelectDate,
  onChangeMonth,
  brandPrimary = "#273240",
}: MiniCalendarProps) {
  const parts = month.split("-").map(Number);
  const year = parts[0] ?? 2024;
  const monthNum = parts[1] ?? 1;

  const firstDay = new Date(year, monthNum - 1, 1).getDay();
  const daysInMonth = new Date(year, monthNum, 0).getDate();

  const monthDisplay = new Date(year, monthNum - 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const goToPrevMonth = () => {
    const prevMonth = monthNum === 1 ? 12 : monthNum - 1;
    const prevYear = monthNum === 1 ? year - 1 : year;
    onChangeMonth(`${prevYear}-${String(prevMonth).padStart(2, "0")}`);
  };

  const goToNextMonth = () => {
    const nextMonth = monthNum === 12 ? 1 : monthNum + 1;
    const nextYear = monthNum === 12 ? year + 1 : year;
    onChangeMonth(`${nextYear}-${String(nextMonth).padStart(2, "0")}`);
  };

  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];

  for (let i = 0; i < firstDay; i++) {
    currentWeek.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  const availableSet = new Set(availableDays);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900">{monthDisplay}</h3>
        <div className="flex gap-1">
          <button
            onClick={goToPrevMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-400 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weeks.flat().map((day, idx) => {
          if (day === null) {
            return <div key={idx} className="aspect-square" />;
          }

          const dateStr = `${year}-${String(monthNum).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isAvailable = availableSet.has(dateStr);
          const isSelected = dateStr === selectedDate;

          return (
            <button
              key={idx}
              onClick={() => isAvailable && onSelectDate(dateStr)}
              disabled={!isAvailable}
              className={`
                aspect-square rounded-lg flex items-center justify-center text-sm font-medium
                transition-all
                ${isAvailable && !isSelected ? "hover:bg-gray-100 text-gray-900" : ""}
                ${!isAvailable ? "text-gray-300 cursor-not-allowed" : ""}
              `}
              style={isSelected ? { backgroundColor: brandPrimary, color: "white" } : undefined}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Payment Form Component (Stripe Elements)
// ────────────────────────────────────────────────────────────────────────────

interface PaymentFormProps {
  clientName: string;
  clientEmail: string;
  onSuccess: (paymentIntentId: string) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  loading?: boolean;
  brandPrimary?: string;
}

function PaymentForm({
  clientName,
  clientEmail,
  onSuccess,
  onError,
  disabled,
  loading,
  brandPrimary = "#273240",
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      onError("Payment system is loading. Please wait a moment and try again.");
      return;
    }

    if (!clientName || !clientEmail) {
      onError("Please fill in your name and email first");
      return;
    }

    if (!isReady) {
      onError("Please enter your card details");
      return;
    }

    setProcessing(true);

    try {
      // First, validate the payment element has complete data
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError(submitError.message || "Please check your payment details");
        setProcessing(false);
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: clientName,
              email: clientEmail,
            },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        // Handle specific error types
        if (error.type === "card_error" || error.type === "validation_error") {
          onError(error.message || "Please check your card details");
        } else {
          onError(error.message || "Payment failed. Please try again.");
        }
      } else if (paymentIntent) {
        switch (paymentIntent.status) {
          case "succeeded":
            onSuccess(paymentIntent.id);
            break;
          case "processing":
            onError("Payment is processing. Please wait...");
            break;
          case "requires_payment_method":
            onError("Please enter valid card details and try again.");
            break;
          case "requires_action":
            // 3D Secure or other action required - Stripe handles this automatically
            onError("Additional verification required. Please complete the verification in the popup.");
            break;
          default:
            onError(`Payment status: ${paymentIntent.status}. Please try again.`);
        }
      } else {
        onError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Payment error:", err);
      onError(err instanceof Error ? err.message : "Payment failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          layout: "tabs",
        }}
        onChange={(event) => {
          setIsReady(event.complete);
        }}
      />
      <button
        type="submit"
        disabled={!stripe || processing || disabled || loading}
        className="w-full mt-4 py-3 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
        style={{ backgroundColor: brandPrimary }}
      >
        {processing || loading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            {processing ? "Processing..." : "Completing booking..."}
          </>
        ) : (
          "Pay & Book"
        )}
      </button>
    </form>
  );
}

